import { useEffect, useState } from 'react';
import { inputClass } from './fieldStyles';

/**
 * Day / Month / Year selector for `date` fields. Replaces the native date
 * input whose calendar only scrolls month-by-month, which is painful for
 * dates far in the past (a date of birth) or future. Picking the year from a
 * dropdown is one tap on mobile and one click (or type-ahead) on desktop, and
 * behaves identically across platforms. The value stays ISO 'YYYY-MM-DD' so
 * storage, validation and exports are unchanged; it is emitted only once all
 * three parts are chosen.
 *
 * The three parts are held in local state: the parent value is empty until the
 * date is complete, so driving the dropdowns straight off it would reset every
 * one the moment you picked a single part.
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Spans a lifetime back plus a margin forward for expiry-style dates.
const THIS_YEAR = new Date().getFullYear();
const YEARS: number[] = [];
for (let y = THIS_YEAR + 15; y >= THIS_YEAR - 100; y--) YEARS.push(y);

interface Parts {
  y: string;
  m: string;
  d: string;
}

function parse(value: string): Parts {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  return m ? { y: m[1]!, m: m[2]!, d: m[3]! } : { y: '', m: '', d: '' };
}

function iso(p: Parts): string {
  return p.y && p.m && p.d ? `${p.y}-${p.m}-${p.d}` : '';
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Days in a month; an unknown year falls back to a leap year so Feb 29 stays. */
function daysInMonth(year: string, month: string): number {
  return new Date(year ? Number(year) : 2000, Number(month), 0).getDate();
}

interface DateSelectProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}

export function DateSelect({ value, onChange, disabled, invalid }: DateSelectProps) {
  const [parts, setParts] = useState<Parts>(() => parse(value));

  // Re-seed from the parent only when it hands us a complete date that differs
  // from what's shown (draft resume, reopen, or a programmatic reset). A blank
  // or partial parent value never wipes a part the user is mid-way through.
  useEffect(() => {
    const incoming = parse(value);
    if (incoming.y && incoming.m && incoming.d && value !== iso(parts)) {
      setParts(incoming);
    }
    // Sync from the prop only; `parts` is read fresh each run via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { y, m, d } = parts;
  const cls = inputClass(invalid ?? false, false);
  const dayMax = m ? daysInMonth(y, m) : 31;

  function update(next: Partial<Parts>) {
    const p: Parts = { ...parts, ...next };
    // Shrinking month (e.g. switching to February) must not leave an
    // impossible day like the 31st selected.
    if (p.m && p.d && Number(p.d) > daysInMonth(p.y, p.m)) {
      p.d = pad(daysInMonth(p.y, p.m));
    }
    setParts(p);
    onChange(iso(p));
  }

  return (
    <div className="mt-1.5 grid grid-cols-3 gap-2">
      <select aria-label="Day" value={d} disabled={disabled} className={cls}
        onChange={(e) => update({ d: e.target.value })}>
        <option value="">Day</option>
        {Array.from({ length: dayMax }, (_, i) => pad(i + 1)).map((dd) => (
          <option key={dd} value={dd}>{Number(dd)}</option>
        ))}
      </select>
      <select aria-label="Month" value={m} disabled={disabled} className={cls}
        onChange={(e) => update({ m: e.target.value })}>
        <option value="">Month</option>
        {MONTHS.map((name, i) => (
          <option key={name} value={pad(i + 1)}>{name}</option>
        ))}
      </select>
      <select aria-label="Year" value={y} disabled={disabled} className={cls}
        onChange={(e) => update({ y: e.target.value })}>
        <option value="">Year</option>
        {YEARS.map((yr) => (
          <option key={yr} value={String(yr)}>{yr}</option>
        ))}
      </select>
    </div>
  );
}
